import { useEffect, useRef, useState } from 'react';
import { ApolloError, useApolloClient } from '@apollo/client';

import useAuthentication from './useAuthentication';
import useShopStore from './useShopStore';
import {
  CHECK_GUEST_CART_QUERY,
  CREATE_EMPTY_CART_MUTATION,
  CUSTOMER_CART_ID_QUERY,
  MERGE_CARTS_MUTATION,
} from './useCartId.queries';

import { CreateEmptyCartMutation } from './__generated__/CreateEmptyCartMutation';
import { CustomerCartIdQuery } from './__generated__/CustomerCartIdQuery';
import {
  MergeCartsMutation,
  MergeCartsMutationVariables,
} from './__generated__/MergeCartsMutation';
import {
  CheckGuestCartQuery,
  CheckGuestCartQueryVariables,
} from './__generated__/CheckGuestCartQuery';

export const GUEST_CART_ID_STORAGE_KEY = 'nicom/shop/guest-cart-id';

type CartUserType = 'customer' | 'guest';

interface UseCartIdHookResult {
  loading: boolean;
  cartId?: string;
  error?: ApolloError;
  cartUserType?: CartUserType;
}

export default function useCartId(): UseCartIdHookResult {
  const client = useApolloClient();
  const { storeCode, loading: storeCodeLoading } = useShopStore();
  const { isAuthenticated, loading: authLoading } = useAuthentication();
  const [result, setResult] = useState<UseCartIdHookResult>({ loading: true });
  const pending = authLoading || storeCodeLoading || !storeCode;
  const cancelToken = useRef(false);

  useEffect(() => {
    if (!pending) {
      setResult((current) => ({ ...current, loading: true, error: undefined }));
      // have some mechanism to prevent state updates if the calling component
      // is unmounted, otherwise many warnings are raised in react dev mode
      cancelToken.current = false;

      const resolvePromise = isAuthenticated
        ? resolveCustomerCartId(client)
        : resolveGuestCartId(client);

      resolvePromise
        .then((result) => {
          if (cancelToken.current) return;
          setResult((current) => ({ ...current, ...result, loading: false }));
        })
        .catch((error) => {
          if (cancelToken.current) return;
          setResult((current) => ({ ...current, error, loading: false }));
        });
    }

    return () => {
      cancelToken.current = true;
    };
  }, [client, isAuthenticated, pending]);

  return result;
}

type ClientType = ReturnType<typeof useApolloClient>;
type ResolvedCart = Pick<
  UseCartIdHookResult,
  'cartId' | 'cartUserType' | 'error'
>;

const resolveGuestCartId = async (
  client: ClientType
): Promise<ResolvedCart> => {
  const storedGuestCartId = getStoredGuestCartId();

  if (storedGuestCartId) {
    try {
      const response = await client.query<
        CheckGuestCartQuery,
        CheckGuestCartQueryVariables
      >({
        query: CHECK_GUEST_CART_QUERY,
        fetchPolicy: 'network-only',
        variables: { id: storedGuestCartId },
      });

      if (!response.data?.magento2?.cart?.id) {
        throw new Error('No id found on guest cart');
      }

      return { cartId: storedGuestCartId, cartUserType: 'guest' };
    } catch (_error) {
      // fail silently since we want to continue requesting a new guest cart id
    }
  }

  const { data } = await client.mutate<CreateEmptyCartMutation>({
    mutation: CREATE_EMPTY_CART_MUTATION,
  });

  const guestCartId = data?.magento2?.createEmptyCart;

  if (!guestCartId) throw new Error('Error creating empty guest cart');

  storeGuestCartId(guestCartId);

  return { cartId: guestCartId, cartUserType: 'guest' };
};

const resolveCustomerCartId = async (
  client: ClientType
): Promise<ResolvedCart> => {
  const { data: customerCartData } = await client.query<CustomerCartIdQuery>({
    query: CUSTOMER_CART_ID_QUERY,
    fetchPolicy: 'network-only',
  });

  const customerCartId = customerCartData?.magento2?.customerCart?.id;

  if (!customerCartId) throw new Error('No id found on customer cart');

  const storedGuestCartId = getStoredGuestCartId();

  if (!storedGuestCartId) {
    return { cartId: customerCartId, cartUserType: 'customer' };
  }

  try {
    const { data: mergeData } = await client.mutate<
      MergeCartsMutation,
      MergeCartsMutationVariables
    >({
      mutation: MERGE_CARTS_MUTATION,
      variables: { sourceId: storedGuestCartId, destinationId: customerCartId },
    });

    const mergedId = mergeData?.magento2?.mergeCarts?.id;

    return mergedId
      ? { cartId: mergedId, cartUserType: 'customer' }
      : {
          cartId: customerCartId,
          cartUserType: 'customer',
          error: new ApolloError({
            errorMessage: 'No id found after merging carts',
          }),
        };
  } catch (error) {
    return { error, cartId: customerCartId, cartUserType: 'customer' };
  } finally {
    removeStoredGuestCartId();
  }
};

// wrapper fns since localStorage not available on node for SSR
const storeGuestCartId = (cartId: string) => {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(GUEST_CART_ID_STORAGE_KEY, cartId);
  }
};

const getStoredGuestCartId = () =>
  typeof localStorage !== 'undefined'
    ? localStorage.getItem(GUEST_CART_ID_STORAGE_KEY)
    : null;

const removeStoredGuestCartId = () =>
  typeof localStorage !== 'undefined'
    ? localStorage.removeItem(GUEST_CART_ID_STORAGE_KEY)
    : null;
